import {
  deleteIndexedDbValue,
  readIndexedDbValue,
  writeIndexedDbValue
} from "./indexedDbCache";
import { errorMessage, isRecord } from "../types/data";

interface CacheEntry<T> {
  value: T;
  timestamp?: number;
  sourceTimestamp?: number;
}
interface RefreshResult<T> { value: T; refreshed: boolean; }
export interface CacheOptions<T> {
  ttl?: number;
  force?: boolean;
  staleTtl?: number;
  staleWhileRevalidate?: boolean;
  onBackgroundUpdate?: (value: T) => void | Promise<void>;
}

const DEFAULT_TTL = 15 * 60 * 1000;
const DEFAULT_STALE_TTL = 24 * 60 * 60 * 1000;
const LOCAL_STORAGE_MAX_BYTES = 20 * 1024;
const INDEXED_DB_ONLY_PREFIXES = [
  "film_user_state_",
  "demo_watch_history_",
  "movie_note_",
  "user_note_",
  "user_profile_",
  "friend_profile_summary_",
  "friend_roster_",
  "user_friend_ids_",
  "layout_user_settings_",
  "my-results-",
  "special_films_",
  "special_list_",
  "level_friend_ids_",
  "friend_recent_activities_",
  "activity_friend_name_map_",
  "friend_stats_",
  "friend_relation_",
  "users_search_"
];

const memoryCache = new Map<string, CacheEntry<unknown>>();
const pendingRequests = new Map<string, { request: Promise<RefreshResult<unknown>> }>();
const pendingInvalidations = new Map<string, Promise<void>>();
const cacheGenerations = new Map<string, number>();

const buildStorageKey = (key: string) => `app_cache_${key}`;
const isIndexedDbUnavailable = (error: unknown) =>
  errorMessage(error).includes("IndexedDB is not available");

const shouldPersistInLocalStorage = (key: string, payload: CacheEntry<unknown>) => {
  if (INDEXED_DB_ONLY_PREFIXES.some((prefix) => String(key).startsWith(prefix))) {
    return false;
  }

  try {
    return JSON.stringify(payload).length <= LOCAL_STORAGE_MAX_BYTES;
  } catch {
    return false;
  }
};

const readMemoryOrLocalEntry = (key: string): CacheEntry<unknown> | null => {
  const memoryEntry = memoryCache.get(key);

  if (
    memoryEntry &&
    Object.prototype.hasOwnProperty.call(memoryEntry, "value")
  ) {
    return memoryEntry;
  }
  if (memoryEntry && !Object.prototype.hasOwnProperty.call(memoryEntry, "value")) {
    memoryCache.delete(key);
  }

  try {
    const raw = localStorage.getItem(buildStorageKey(key));
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || !Object.prototype.hasOwnProperty.call(parsed, "value")) {
      localStorage.removeItem(buildStorageKey(key));
      return null;
    }

    const entry = parsed as unknown as CacheEntry<unknown>;
    memoryCache.set(key, entry);
    return entry;
  } catch (error) {
    console.warn(`Failed to read cache for ${key}:`, error);
    return null;
  }
};

// Each cache key identifies one value type; callers own that key's schema/version.
export const readCachedValue = <T = unknown>(key: string, ttl = DEFAULT_TTL): T | null => {
  const entry = readMemoryOrLocalEntry(key);
  const sourceTimestamp = Number(entry?.sourceTimestamp ?? entry?.timestamp ?? 0);
  if (!entry || Date.now() - sourceTimestamp > ttl) return null;
  return entry.value as T;
};

export const writeCachedValue = <T>(
  key: string,
  value: T,
  { preserveTimestamp = false }: { preserveTimestamp?: boolean } = {}
) => {
  if (value === undefined) {
    memoryCache.delete(key);
    return;
  }

  const previousEntry = preserveTimestamp ? readMemoryOrLocalEntry(key) : null;
  const timestamp = Date.now();
  const payload = {
    value,
    // `timestamp` keeps the fallback alive after a confirmed local write,
    // while `sourceTimestamp` still records the last full source refresh.
    timestamp,
    sourceTimestamp: preserveTimestamp
      ? Number(previousEntry?.sourceTimestamp ?? previousEntry?.timestamp ?? 0)
      : timestamp
  };

  memoryCache.set(key, payload);

  try {
    if (shouldPersistInLocalStorage(key, payload)) {
      localStorage.setItem(buildStorageKey(key), JSON.stringify(payload));
    } else {
      localStorage.removeItem(buildStorageKey(key));
    }
  } catch (error) {
    // IndexedDB is the durable cache. localStorage remains only as a fast
    // compatibility layer for already small, non-personal values.
  }

  const pendingInvalidation = pendingInvalidations.get(key);
  void Promise.resolve(pendingInvalidation)
    .catch(() => null)
    .then(() => writeIndexedDbValue(buildStorageKey(key), payload))
    .catch((error: unknown) => {
      if (isIndexedDbUnavailable(error)) return;
      console.warn(`Failed to write IndexedDB cache for ${key}:`, error);
    });
};

export const invalidateCachedValue = (key: string) => {
  cacheGenerations.set(key, Number(cacheGenerations.get(key) || 0) + 1);
  memoryCache.delete(key);
  pendingRequests.delete(key);

  try {
    localStorage.removeItem(buildStorageKey(key));
  } catch (error) {
    console.warn(`Failed to invalidate cache for ${key}:`, error);
  }

  const invalidation = deleteIndexedDbValue(buildStorageKey(key))
    .catch((error: unknown) => {
      if (isIndexedDbUnavailable(error)) return;
      console.warn(`Failed to invalidate IndexedDB cache for ${key}:`, error);
    });
  pendingInvalidations.set(key, invalidation);
  void invalidation.finally(() => {
    if (pendingInvalidations.get(key) === invalidation) {
      pendingInvalidations.delete(key);
    }
  });
  return invalidation;
};

const readPersistentCacheEntry = async (key: string): Promise<CacheEntry<unknown> | null> => {
  const pendingInvalidation = pendingInvalidations.get(key);
  if (pendingInvalidation) await pendingInvalidation;
  const fastEntry = readMemoryOrLocalEntry(key);
  if (fastEntry) return fastEntry;

  try {
    const entry = await readIndexedDbValue<CacheEntry<unknown>>(buildStorageKey(key));
    if (!entry) return null;
    if (!Object.prototype.hasOwnProperty.call(entry, "value")) return null;
    memoryCache.set(key, entry);
    return entry;
  } catch (error) {
    if (isIndexedDbUnavailable(error)) return null;
    console.warn(`Failed to read IndexedDB cache for ${key}:`, error);
    return null;
  }
};

export const getCachedOrFetch = async <T>(
  key: string,
  fetcher: () => T | Promise<T>,
  {
    ttl = DEFAULT_TTL,
    force = false,
    staleTtl = Math.max(ttl, DEFAULT_STALE_TTL),
    staleWhileRevalidate = false,
    onBackgroundUpdate
  }: CacheOptions<T> = {}
): Promise<T> => {
  const cachedEntry = await readPersistentCacheEntry(key);
  const cacheAge = cachedEntry
    ? Date.now() - Number(
      cachedEntry.sourceTimestamp ?? cachedEntry.timestamp ?? 0
    )
    : Infinity;
  const staleCacheAge = cachedEntry
    ? Date.now() - Number(cachedEntry.timestamp || 0)
    : Infinity;

  if (!force && cachedEntry && cacheAge <= ttl) {
    return cachedEntry.value as T;
  }

  let pendingEntry = pendingRequests.get(key);
  if (!pendingEntry) {
    const generation = Number(cacheGenerations.get(key) || 0);
    const request = (async () => {
      try {
        const value = await fetcher();
        const isCurrentGeneration =
          Number(cacheGenerations.get(key) || 0) === generation;
        if (value !== undefined && isCurrentGeneration) {
          writeCachedValue(key, value);
        }
        return {
          value,
          refreshed: isCurrentGeneration
        };
      } catch (error) {
        // The cache is only a fallback; Firestore remains the source of truth.
        // Keep a recent stale value when the network is unavailable instead of
        // deleting it before the request and rendering an empty page.
        if (cachedEntry && staleCacheAge <= staleTtl) {
          return { value: cachedEntry.value, refreshed: false };
        }
        throw error;
      }
    })();

    pendingEntry = { request };
    pendingRequests.set(key, pendingEntry);
    void request.then(
      () => {
        if (pendingRequests.get(key) === pendingEntry) {
          pendingRequests.delete(key);
        }
      },
      () => {
        if (pendingRequests.get(key) === pendingEntry) {
          pendingRequests.delete(key);
        }
      }
    );
  }

  if (
    !force &&
    staleWhileRevalidate &&
    cachedEntry &&
    staleCacheAge <= staleTtl
  ) {
    if (typeof onBackgroundUpdate === "function") {
      void pendingEntry.request.then((result) => {
        if (!result.refreshed) return null;
        // Let the stale return value render first even when a cache-backed
        // Firestore request resolves in the same microtask turn.
        return Promise.resolve().then(() => onBackgroundUpdate(result.value as T));
      }).catch(() => null);
    }
    return cachedEntry.value as T;
  }

  const result = await pendingEntry.request;
  return result.value as T;
};
